import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { homeFor } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { GraduationCap, Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(homeFor(user.role), { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Benvenuto, ${res.user.name || res.user.email}`);
      navigate(homeFor(res.user.role), { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const quickFill = (kind) => {
    if (kind === "admin") { setEmail("web@centrostudiathena.it"); setPassword("Athena2026!"); }
    else { setEmail("partner@demo.athena.it"); setPassword("Partner2026!"); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 brand-gradient text-white">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-white/95 flex items-center justify-center athena-shadow p-1.5">
            <img src="/logo-athena.png" alt="Centro Studi Athena" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display text-xl font-bold">Athena Partner Portal</div>
            <div className="text-sm text-slate-300">Centro Studi Athena</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            Gestisci la tua rete formativa
          </h1>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Un unico portale per catalogo corsi, calendario edizioni, ordini partner, iscrizioni corsisti,
            pagamenti e reportistica — pensato per gli affiliati Centro Studi Athena.
          </p>
          <div className="mt-8 grid gap-3 text-sm">
            {["Catalogo & Calendario formativo", "Ordini + iscrizioni multi-corsista", "Import massivo CSV / Excel", "Pagamenti online & pro-forma"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-slate-200">
                <ShieldCheck className="w-4 h-4 text-amber-400" /> {t}
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-400">© {new Date().getFullYear()} Centro Studi Athena · GDPR compliant</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <Card className="w-full max-w-md athena-shadow border-slate-200">
          <CardContent className="p-8">
            <div className="lg:hidden mb-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center p-1">
                <img src="/logo-athena.png" alt="Centro Studi Athena" className="w-full h-full object-contain" />
              </div>
              <div className="font-display font-bold text-lg">Athena Portal</div>
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Accedi al portale</h2>
            <p className="text-sm text-slate-500 mt-1">Inserisci le credenziali fornite da Centro Studi Athena.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required data-testid="login-email-input" placeholder="nome@azienda.it" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/password-dimenticata" className="text-xs font-semibold text-amber-600 hover:text-amber-700" data-testid="login-forgot-link">Password dimenticata?</Link>
                </div>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required data-testid="login-password-input" placeholder="••••••••" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#0F2942] hover:bg-[#1B3B5F]"
                data-testid="login-submit-button">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
